import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type {
  PublicQuestionDetail,
  PublicReply,
  PublicRfiProjection,
} from '@find-my-part/types';
import { widgetApi } from '../api/client';
import {
  loadPublicQuestionDetail,
  peekPublicQuestionDetail,
  publicDetailCacheKey,
  revalidatePublicQuestionDetail,
} from '../api/publicQuestionCache';

export interface UseQuestionDetailResult {
  question: PublicQuestionDetail | null;
  replies: PublicReply[];
  /**
   * Display-safe public RFI "still needed" projection (PR D — embedded on the
   * detail response), or `null` when the question has no non-terminal RFI.
   * Cleared to `null` on error / non-success / empty questionId so stale staff
   * prose never leaks across a question change or a failed refetch.
   */
  rfi: PublicRfiProjection | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface QuestionDetailState {
  /** Includes resource identity plus initialisation posture. */
  key: string;
  question: PublicQuestionDetail | null;
  replies: PublicReply[];
  rfi: PublicRfiProjection | null;
  isLoading: boolean;
  error: string | null;
}

export function useQuestionDetail(questionId: string): UseQuestionDetailResult {
  const initialized = widgetApi.isInitialized();
  const cacheKey = publicDetailCacheKey(questionId);
  const renderKey = `${questionId ? 'identified' : 'empty'}|${initialized ? 'ready' : 'uninitialised'}|${cacheKey}`;

  // As with the list hook, scope renderable state to the CURRENT key before an
  // effect can run. This prevents question A from committing once under
  // question B, while preserving a warm question B in its first commit.
  const keySnapshot = useMemo<QuestionDetailState>(() => {
    if (!questionId || !initialized) {
      return {
        key: renderKey,
        question: null,
        replies: [],
        rfi: null,
        isLoading: false,
        error: null,
      };
    }

    const cached = peekPublicQuestionDetail(questionId);
    return {
      key: renderKey,
      question: cached.data?.question ?? null,
      replies: cached.data?.replies ?? [],
      rfi: cached.data?.rfi ?? null,
      isLoading: !cached.data,
      error: null,
    };
  }, [renderKey, questionId, initialized]);

  const [state, setState] = useState<QuestionDetailState>(keySnapshot);
  const visibleState = state.key === renderKey ? state : keySnapshot;
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * ADR-028 Slice 3. `force` marks an explicit visitor retry — always a real
   * network attempt, still de-duplicated against an identical in-flight read.
   */
  const fetchDetail = useCallback(
    async (force = false) => {
      if (!questionId || !initialized) {
        requestIdRef.current += 1;
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setState({
          key: renderKey,
          question: null,
          replies: [],
          rfi: null,
          isLoading: false,
          error: null,
        });
        return;
      }

      // Warm entry adopted before any state transition: returning to a question
      // already read in this session paints instantly with no skeleton.
      if (!force) {
        const cached = peekPublicQuestionDetail(questionId);
        if (cached.data) {
          const currentRequestId = ++requestIdRef.current;
          abortControllerRef.current?.abort();
          abortControllerRef.current = null;
          setState({
            key: renderKey,
            question: cached.data.question,
            replies: cached.data.replies,
            rfi: cached.data.rfi ?? null,
            error: null,
            isLoading: false,
          });

          if (cached.freshness === 'stale') {
            // Adopt the ONE shared background refresh. Not paired with a
            // `load()` follow-up, which would bypass the failed-revalidation
            // cooldown; `null` means the cache is declining and the stale data
            // already on screen stands.
            const pending = revalidatePublicQuestionDetail(questionId);
            if (pending) {
              void pending.then((result) => {
                // Generation guard: a background refresh for a question the
                // visitor has already left must never paint over the current one.
                if (requestIdRef.current !== currentRequestId) return;
                if (!result.admitted) return;
                setState((current) =>
                  current.key === renderKey
                    ? {
                        ...current,
                        question: result.data.question,
                        replies: result.data.replies,
                        rfi: result.data.rfi ?? null,
                      }
                    : current
                );
              }, () => undefined);
            }
          }
          return;
        }
      }

      const currentRequestId = ++requestIdRef.current;
      // Local-only controller. It is never handed to the shared flight, so
      // leaving this question cannot cancel a read another consumer awaits — and
      // equally, it does not cancel the network request at all. Its only job is
      // to mark that this hook instance has moved on, so a late response is
      // ignored rather than applied.
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // A miss includes an entry that crossed MAX_AGE while the document sat
      // in BFCache. Clear it before the blocking reload so no consumer can
      // observe question A (or expired question B) as the current resource.
      setState({
        key: renderKey,
        question: null,
        replies: [],
        rfi: null,
        error: null,
        isLoading: true,
      });

      try {
        const response = await loadPublicQuestionDetail(questionId, { force });
        if (controller.signal.aborted || requestIdRef.current !== currentRequestId) return;

        if (response.success) {
          setState({
            key: renderKey,
            question: response.data.question,
            replies: response.data.replies,
            rfi: response.data.rfi ?? null,
            error: null,
            isLoading: false,
          });
        } else {
          setState({
            key: renderKey,
            question: null,
            replies: [],
            rfi: null,
            error: response.error.message,
            isLoading: false,
          });
        }
      } catch (err) {
        if (controller.signal.aborted || requestIdRef.current !== currentRequestId) return;
        setState({
          key: renderKey,
          question: null,
          replies: [],
          rfi: null,
          error: err instanceof Error ? err.message : 'Failed to load question',
          isLoading: false,
        });
      } finally {
        if (!controller.signal.aborted && requestIdRef.current === currentRequestId) {
          setState((current) =>
            current.key === renderKey ? { ...current, isLoading: false } : current
          );
        }
      }
    },
    [questionId, initialized, renderKey]
  );

  useEffect(() => {
    void fetchDetail();
    const handlePageShow = (event: PageTransitionEvent): void => {
      // A BFCache restore does not remount React. Re-enter the ordinary cache
      // path so document suspension cannot bypass fresh/stale/expired policy.
      if (event.persisted) void fetchDetail();
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      abortControllerRef.current?.abort();
    };
  }, [fetchDetail]);

  const refetch = useCallback(() => fetchDetail(true), [fetchDetail]);

  return {
    question: visibleState.question,
    replies: visibleState.replies,
    rfi: visibleState.rfi,
    isLoading: visibleState.isLoading,
    error: visibleState.error,
    refetch,
  };
}
